import { DiscussionActivity } from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import { useDiscussionEntryHeader } from '../../hooks/useDiscussionEntryHeader';

import type { User } from '@/types/directory/User';
import type { KeyResultChange, ObjectiveComment } from '@/types/objectives';

import i18n from '@/plugins/i18n';
import { formatKeyResultValue } from '@/utils/objectivesFormat';

interface ObjectiveMeasureUpdateCardProps {
    comment: ObjectiveComment;
    /** The move the server journaled; the caller narrows on `comment.kind` before passing it. */
    change: KeyResultChange;
    /** Whoever moved the value, resolved from `comment.authorId` by the caller. */
    author?: User;
}

/**
 * One automatic entry in the "Discussion" thread: the server appended it when someone moved a key
 * result's value. Reads as one line of prose ("Alice moved “Signed contracts” from 10 / 50 to
 * 24 / 50"), with the time of day since several can land within a single day. Values are worded
 * for the reader (OUI/NON, %, currency) from the raw numbers the server keeps.
 */
export default function ObjectiveMeasureUpdateCard({
    comment,
    change,
    author,
}: ObjectiveMeasureUpdateCardProps) {
    const { t } = useTranslation();
    const header = useDiscussionEntryHeader({
        author,
        // The server moves `updatedAt` each time it folds a later move of the series into the entry.
        date: comment.updatedAt,
        withTimeOfDay: true,
    });

    const yesNoLabels = { yes: t('objectives.form.yes'), no: t('objectives.form.no') };
    const quote = (value: number) =>
        formatKeyResultValue({
            value,
            unit: change.unit,
            currencySymbol: change.currencySymbol,
            targetValue: change.targetValue,
            language: i18n.language,
            yesNoLabels,
        });

    return (
        <DiscussionActivity
            {...header}
            label={t('objectives.detail.discussion.journal.entryLabel')}
            dataTestId={`objective-discussion-measure-update-${comment.id}`}
        >
            {t('objectives.detail.discussion.journal.moved', {
                title: change.title,
                from: quote(change.fromValue),
                to: quote(change.toValue),
            })}
        </DiscussionActivity>
    );
}
